import React from 'react';
import get from 'lodash.get';
import queryString from 'qs';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { of, concat, EMPTY } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';

import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { AdvancedSearch } from '../../components/shipping-grid/views/advanced-search';
import {
  RETURNS_VIEW_ID,
  END_OF_DAY_VIEW_ID,
  USPS_PICKUP_VIEW_ID,
  BATCHES_VIEW_ID,
  FULFILLMENTS_VIEW_ID,
  FULFILLMENTS_RECENT_VIEW_ID,
  FULFILLMENTS_PENDING_VIEW_ID,
  FULFILLMENTS_IN_TRANSIT_VIEW_ID,
  FULFILLMENTS_DELIVERED_VIEW_ID,
  FULFILLMENTS_DELIVERY_EXCEPTION_VIEW_ID,
  FULFILLMENTS_CANCELLED_VIEW_ID,
  FULFILLMENTS_ADVANCED_SEARCH_VIEW_ID,
} from '../../components/shipping-grid/views/view-id-constants';
import { UserSettings } from '../../enums/user-settings/user-settings';
import allFilterTypes from '../../pages/shipping-page/shipping-filter-bar-container/filter-options/options';
import { BulkErrorsAndWarningsService } from '../../services/bulk-errors-and-warnings-service';
import { ColumnOrderByParamService } from '../../services/column-orderby-param-service';
import { mapShipmentsFiltersToRequestBody } from '../../services/filtering/api-param-map-service';
import { ShipmentsGridViewService } from '../../services/shipments-grid-view-service';
import * as BatchDetailActions from '../../store/batches/details/batch.details.actions';
import { bulkErrorsAndWarningsModalObservable } from '../../store/bulk-errors-and-warnings/bulk-errors-and-warnings.observables';
import Global from '../../store/global-selectors';
import { FilterViewProps } from '../../store/shipments/filtering/filterview.properties';
import * as ShipmentsFilteringActions from '../../store/shipments/filtering/shipments.filtering.actions';
import * as GridActions from '../../store/shipments/grid/shipments.grid.actions';
import * as NavActions from '../../store/shipments/nav/shipments.nav.actions';
import { messages } from './messages';
import * as Actions from './shipments.actions';
import * as ShipmentsApi from './shipments.api';
import { cleanseFillIdsWithoutFulfillmentPlan } from './shipments.helpers';
import { saveUserSetting } from '../user/user.actions';
import { getSelectedViewFromPathName } from '../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import { noop } from 'ui-components/default-props/noop';

const { id: advancedSearchId } = AdvancedSearch;
let lastRequestGuid = GuidService.newGuid();

const preventShippingGridCallViewIDs = [
  FULFILLMENTS_VIEW_ID,
  FULFILLMENTS_RECENT_VIEW_ID,
  FULFILLMENTS_PENDING_VIEW_ID,
  FULFILLMENTS_IN_TRANSIT_VIEW_ID,
  FULFILLMENTS_DELIVERED_VIEW_ID,
  FULFILLMENTS_DELIVERY_EXCEPTION_VIEW_ID,
  FULFILLMENTS_CANCELLED_VIEW_ID,
  FULFILLMENTS_ADVANCED_SEARCH_VIEW_ID,
  advancedSearchId,
];

const mapAppliedFiltersToCurrentFilters = (appliedFilters = []) => {
  return appliedFilters.map((appliedFilter) => {
    const filterType = allFilterTypes.find((f) => f.id === appliedFilter.filterById);

    return {
      ...appliedFilter,
      ...filterType,
    };
  });
};

export const getShipmentDataEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.GET_SHIPMENTS_REQUEST, Actions.GET_SILENT_SHIPMENTS_REQUEST, NavActions.SHIPMENTS_SEARCH),
    mergeMap(() => {
      const state = getState();
      const statusViewId = getSelectedViewFromPathName(history?.location?.pathname)?.id || '';
      if (statusViewId.includes(BATCHES_VIEW_ID)) {
        return of(BatchDetailActions.reloadBatchDetails());
      }

      if (preventShippingGridCallViewIDs.some((viewId) => statusViewId.includes(viewId))) {
        return EMPTY;
      }

      if (!ShipmentsGridViewService.isValidViewId(statusViewId)) {
        return EMPTY;
      }

      const { searchTerm } = Global.selectShipmentsNavSearchData(state);
      const statePage = Global.selectShipmentsGridPage(state);
      const statePageSize = Global.selectShipmentsGridPageSize(state);
      const activeFilterView = Global.selectShipmentsFilteringActiveFilterView(state);
      const activeFilterViewId = activeFilterView.filterViewId;
      const appliedFilters = mapAppliedFiltersToCurrentFilters(activeFilterView.appliedFilters || []);
      const filterRequest = mapShipmentsFiltersToRequestBody(appliedFilters);
      const orderByColumn = Global.selectShipmentsGridCurrentOrderByColumn(state, {
        statusViewId,
        activeFilterViewId,
      });
      const orderByParams = ColumnOrderByParamService.getShippingGridOrderByApiParamOrDefault(orderByColumn, true);

      const myRequestGuid = GuidService.newGuid();
      lastRequestGuid = myRequestGuid;
      const checkRequestId = () => myRequestGuid === lastRequestGuid;

      return ShipmentsApi.getShipmentsSimple({
        shipmentStatus: statusViewId,
        page: statePage,
        pageSize: statePageSize,
        filter: {
          ...filterRequest,
        },
        ...orderByParams,
        searchTerm,
      }).pipe(
        mergeMap((data) => {
          if (!checkRequestId()) {
            return EMPTY;
          }

          const { page, pageSize, totalCount, currentPageFulfillmentIds, fulfillmentPlans, fulfillments } = data;

          // this is to filter out fulfillmentIds that have no fulfillmentPlanId attached, causes insidious bugs later
          const cleansedcurrentPageFulfillmentIds = cleanseFillIdsWithoutFulfillmentPlan({
            currentPageFulfillmentIds,
            fulfillments,
            fulfillmentPlans,
          });

          const updateFilterViewCount = ShipmentsFilteringActions.changeFilterViewProp(
            activeFilterViewId,
            FilterViewProps.FILTERVIEW_FILTER_COUNT,
            totalCount,
          );
          const updateCustomViewCount = ShipmentsFilteringActions.changeCustomFilterViewProp(
            FilterViewProps.FILTERVIEW_FILTER_COUNT,
            totalCount,
          );
          const updateCountAction = activeFilterViewId === 'custom' ? updateCustomViewCount : updateFilterViewCount;

          return of(Actions.getShipmentsSuccess([], page, pageSize, totalCount, cleansedcurrentPageFulfillmentIds), updateCountAction);
        }),
        catchError((error) => {
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: <FormattedMessage {...messages.genericFallbackErrorMessage} />,
            dontHide: true,
          });
          return of(Actions.getShipmentsFailure(error));
        }),
      );
    }),
  );
};

export const changeShipmentsPage = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(GridActions.SHIPMENT_GRID_CHANGE_PAGE),
    mergeMap(() => {
      const state = getState();
      const userSettingPageSize = Global.selectUserUserSetting(state, {
        userSetting: UserSettings.shipmentsGridPageSize.settingName,
      });
      const statePage = Global.selectShipmentsGridPage(state);
      const statePageSize = Global.selectShipmentsGridPageSize(state);
      const pathname = history.location.pathname || '/shipments';
      const search = queryString.stringify({
        page: statePage,
        pageSize: statePageSize,
      });

      const updatePageSize$ =
        userSettingPageSize !== statePageSize
          ? of(
              saveUserSetting({
                settingName: UserSettings.shipmentsGridPageSize.settingName,
                settingValue: statePageSize,
                excludeV3prefix: true,
              }),
            )
          : EMPTY;

      push({ pathname, search });
      return concat(of(Actions.requestGetShipments()), updatePageSize$);
    }),
  );
};

const viewsWithoutSearchParams = [END_OF_DAY_VIEW_ID, RETURNS_VIEW_ID, USPS_PICKUP_VIEW_ID];
// SPD-14016 This is a clear candidate and much of the logic here just disappears using a more declarative approach
export const changeShipmentsUrl = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(NavActions.SHIPMENTS_CHANGE_VIEW),
    mergeMap(({ viewId }) => {
      if (viewId === advancedSearchId) {
        return EMPTY;
      }

      const state = getState();
      const statePage = Global.selectShipmentsGridPage(state);
      const statePageSize = Global.selectShipmentsGridPageSize(state);
      const pathname = history.location.pathname || '';
      const searchPerformed = Global.selectShipmentsNavSearchPerformed(state);
      const { searchTerm } = Global.selectShipmentsNavSearchData(state);
      const search = queryString.stringify({
        page: statePage,
        pageSize: statePageSize,
        quickSearch: searchPerformed ? searchTerm : undefined,
      });

      let viewIdWithoutDashes = viewId;
      if (viewId.includes(BATCHES_VIEW_ID) || viewId.includes(RETURNS_VIEW_ID) || viewId.includes(FULFILLMENTS_VIEW_ID)) {
        viewIdWithoutDashes = viewId.replace('-', '/');
      }

      // I suspect this won't be needed once our routing state isn't duplicated in redux.
      const correctedPathname = pathname.includes(viewIdWithoutDashes) ? pathname : `/shipments/${viewIdWithoutDashes}`;

      if (viewsWithoutSearchParams.includes(viewIdWithoutDashes)) {
        push({ pathname: correctedPathname });
        return EMPTY;
      }

      push({ pathname: correctedPathname, search });
      return EMPTY;
    }),
  );
};

export const assignBulkFulfillmentPlanToUser = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ASSIGN_BULK_FULFILLMENT_PLAN_TO_USER),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds');
      const userId = get(action, 'userId');
      return ShipmentsApi.assignBulkFulfillmentPlanToUser(fulfillmentPlanIds, userId).pipe(
        mergeMap((data) => {
          const returnActions$ = [];
          const { unassignableFulfillmentPlans, assignedFulfillmentPlans } = data;

          if (assignedFulfillmentPlans.length !== 0) {
            returnActions$.push(of(Actions.silentRequestGetShipments()));
          }
          if (unassignableFulfillmentPlans.length !== 0) {
            const unassigned = unassignableFulfillmentPlans.map((fp) => {
              const { assignedUserId } = fp;
              const assignedUser = Global.selectSellerUsers(getState()).find((u) => u.userId === assignedUserId);
              return {
                ...fp,
                assignedUserName: assignedUser ? assignedUser.name : '',
              };
            });
            const errorsAndWarnings = [BulkErrorsAndWarningsService.getFulfillmentPlanAssignmentErrors(unassigned)];

            const errorModal$ = bulkErrorsAndWarningsModalObservable({
              continueCanBeDisabled: false,
              dataType: 'batchedFulfillmentPlan',
              errorsAndWarnings,
              fulfillmentPlanSetId: undefined,
              hasAdditionalAction: false,
              hasSelectedItems: !!unassignableFulfillmentPlans.length,
              isBatchPrinting: false,
              onContinueAction: noop,
              selectedItems: unassignableFulfillmentPlans,
              title: BulkErrorsAndWarningsService.getFulfillmentPlanAssignmentErrorTitle(),
            });
            returnActions$.push(errorModal$);
          }

          return concat(...returnActions$);
        }),
      );
    }),
  );
};

export const scheduleUspsPickup = (action$) => {
  return action$.pipe(
    ofType(Actions.SCHEDULE_USPS_PICKUP),
    mergeMap((action) => {
      return ShipmentsApi.createUspsPickup(action.payload).pipe(
        tap(() => {
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: <FormattedMessage {...messages.scheduleUspsSuccessNotificationMessage} />,
            duration: 3000,
          });
        }),
        mergeMap(() => EMPTY),
        catchError((err) => {
          const { Errors } = err.response.data;
          const errorDisplay = Errors.length ? (
            Errors[0].Message
          ) : (
            <FormattedMessage {...messages.uspsPickupFallbackErrorNotificationMessage} />
          );
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: errorDisplay,
            duration: 3000,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const cancelUspsPickup = (action$) => {
  return action$.pipe(
    ofType(Actions.CANCEL_USPS_PICKUP),
    mergeMap((action) => {
      return ShipmentsApi.cancelUspsPickup(action.payload).pipe(
        tap(() => {
          return NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: <FormattedMessage {...messages.cancelUspsSuccessNotificationMessage} />,
            duration: 3000,
          });
        }),
        mergeMap(() => EMPTY),
        catchError((err) => {
          const { Errors } = err.response.data;
          const errorDisplay = Errors.length ? Errors[0].Message : <FormattedMessage {...messages.genericFallbackErrorMessage} />;
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: errorDisplay,
            duration: 3000,
          });
          return EMPTY;
        }),
      );
    }),
  );
};
